<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
    <h4 class="text-md font-medium mb-3 text-gray-900 dark:text-white">Preview - Como aparecerá para o paciente</h4>
    
    <div v-if="question.imageUrl" class="space-y-4">
      <div class="text-sm text-gray-600 dark:text-gray-400">
        {{ question.drawingInstructions || 'Use as ferramentas abaixo para marcar a imagem conforme solicitado.' }}
      </div>
      
      <div class="relative border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white">
  <canvas 
    ref="drawingCanvas"
    :width="canvasWidth"
    :height="canvasHeight"
    class="relative z-20 block cursor-crosshair"  @mousedown="startDrawing"
    @mousemove="draw"
    @mouseup="stopDrawing"
    @mouseleave="stopDrawing"
    @touchstart="handleTouch"
    @touchmove="handleTouch"
    @touchend="stopDrawing"
  ></canvas>
  
  <img 
    ref="baseImage"
    :src="question.imageUrl" 
    :alt="question.imageAlt"
    class="absolute top-0 left-0 w-full h-full object-contain pointer-events-none"
    @load="setupCanvas"
  />
</div>
      
      <!-- Ferramentas de desenho -->
      <div class="flex flex-wrap gap-2 items-center justify-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div v-if="question.allowDrawing" class="flex items-center gap-2">
          <button 
            @click="setTool('pen')"
            :class="['px-3 py-1 rounded text-sm', currentTool === 'pen' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300']"
          >
            ✏️ Caneta
          </button>
        </div>
        
        <div v-if="question.allowShapes" class="flex items-center gap-2">
          <button 
            @click="setTool('circle')"
            :class="['px-3 py-1 rounded text-sm', currentTool === 'circle' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300']"
          >
            ⭕ Círculo
          </button>
          <button 
            @click="setTool('rectangle')"
            :class="['px-3 py-1 rounded text-sm', currentTool === 'rectangle' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300']"
          >
            ⬜ Retângulo
          </button>
        </div>
        
        <div class="flex items-center gap-2">
          <button 
            @click="clearCanvas"
            class="px-3 py-1 rounded text-sm bg-red-500 hover:bg-red-600 text-white"
          >
            🗑️ Limpar
          </button>
        </div>
        
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600 dark:text-gray-400">Cor:</label>
          <div 
            class="w-6 h-6 rounded border-2 border-gray-300 cursor-pointer"
            :style="{ backgroundColor: question.drawingColor }"
            @click="showColorPicker = !showColorPicker"
          ></div>
        </div>
        
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600 dark:text-gray-400">Espessura:</label>
          <span class="text-sm font-medium">{{ question.lineWidth }}px</span>
        </div>
      </div>
      
      <!-- Seletor de cores (se necessário) -->
      <div v-if="showColorPicker" class="flex gap-2 justify-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
        <div 
          v-for="color in colorOptions" 
          :key="color"
          class="w-8 h-8 rounded cursor-pointer border-2"
          :class="question.drawingColor === color ? 'border-gray-800 dark:border-white' : 'border-gray-300'"
          :style="{ backgroundColor: color }"
          @click="selectColor(color)"
        ></div>
      </div>
    </div>
    
    <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
      <span class="text-4xl">🖼️</span>
      <p class="mt-2">Adicione uma imagem para ver o preview</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, defineProps, defineEmits } from 'vue';

const props = defineProps({
  question: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['update-question']);

// Refs para canvas e imagem
const drawingCanvas = ref(null);
const baseImage = ref(null);

// Estado do desenho
const isDrawing = ref(false);
const currentTool = ref('pen');
const showColorPicker = ref(false);
const canvasWidth = ref(400);
const canvasHeight = ref(300);

// Cores disponíveis
const colorOptions = ['#ff0000', '#0000ff', '#00ff00', '#ffff00', '#ff00ff', '#000000', '#ffffff'];

// Posição do mouse/touch
let lastX = 0;
let lastY = 0;

function setupCanvas() {
  nextTick(() => {
    if (!drawingCanvas.value || !baseImage.value) return;
    
    const canvas = drawingCanvas.value;
    const img = baseImage.value;
    
    // Ajustar tamanho do canvas baseado na imagem
    const rect = img.getBoundingClientRect();
    canvasWidth.value = rect.width;
    canvasHeight.value = rect.height;
    
    // Configurar contexto do canvas
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = props.question.drawingColor;
    ctx.lineWidth = props.question.lineWidth;
  });
}

function setTool(tool) {
  currentTool.value = tool;
}

function selectColor(color) {
  // Emitir evento para atualizar a cor na pergunta (sem mutar props diretamente)
  emit('update-question', { ...props.question, drawingColor: color });
  showColorPicker.value = false;
  
  // Atualizar contexto do canvas
  if (drawingCanvas.value) {
    const ctx = drawingCanvas.value.getContext('2d');
    ctx.strokeStyle = color;
  }
}

function getMousePos(e) {
  const canvas = drawingCanvas.value;
  const rect = canvas.getBoundingClientRect();
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  };
}

function startDrawing(e) {
  if (!props.question.allowDrawing && currentTool.value === 'pen') return;
  if (!props.question.allowShapes && ['circle', 'rectangle'].includes(currentTool.value)) return;
  
  isDrawing.value = true;
  const pos = getMousePos(e);
  lastX = pos.x;
  lastY = pos.y;
}

function draw(e) {
  if (!isDrawing.value) return;
  
  const canvas = drawingCanvas.value;
  const ctx = canvas.getContext('2d');
  const pos = getMousePos(e);
  
  if (currentTool.value === 'pen') {
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    lastX = pos.x;
    lastY = pos.y;
  }
}

function stopDrawing(e) {
  if (!isDrawing.value) return;
  
  const canvas = drawingCanvas.value;
  const ctx = canvas.getContext('2d');
  const pos = getMousePos(e);
  
  if (currentTool.value === 'circle') {
    const radius = Math.sqrt(Math.pow(pos.x - lastX, 2) + Math.pow(pos.y - lastY, 2));
    ctx.beginPath();
    ctx.arc(lastX, lastY, radius, 0, 2 * Math.PI);
    ctx.stroke();
  } else if (currentTool.value === 'rectangle') {
    ctx.beginPath();
    ctx.rect(lastX, lastY, pos.x - lastX, pos.y - lastY);
    ctx.stroke();
  }
  
  isDrawing.value = false;
}

function handleTouch(e) {
  e.preventDefault();
  const touch = e.touches[0];
  const mouseEvent = new MouseEvent(e.type === 'touchstart' ? 'mousedown' : 
                                   e.type === 'touchmove' ? 'mousemove' : 'mouseup', {
    clientX: touch.clientX,
    clientY: touch.clientY
  });
  
  if (e.type === 'touchstart') startDrawing(mouseEvent);
  else if (e.type === 'touchmove') draw(mouseEvent);
  else if (e.type === 'touchend') stopDrawing(mouseEvent);
}

function clearCanvas() {
  if (drawingCanvas.value) {
    const ctx = drawingCanvas.value.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth.value, canvasHeight.value);
  }
}

onMounted(() => {
  setupCanvas();
});
</script>
